import { createFileRoute, Link, Outlet, useNavigate, useRouterState, redirect } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useDeckGroupSession } from '@/queries/deck-groups';
import { QuizSession } from '@/components/QuizSession';
import { CircularLoader } from '@/components/CircularLoader';
import type { Question } from '@/types';
import styles from '../quizletId.module.scss';

export const Route = createFileRoute('/quizlets/deck-groups/$groupId')({
  component: DeckGroupSession,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function DeckGroupSession() {
  const { groupId } = Route.useParams();
  const navigate = useNavigate();
  const routerState = useRouterState();

  // Check if we're on a child route (like /edit)
  const isChildRoute = routerState.location.pathname.endsWith('/edit');

  // Only fetch session data when not on a child route
  const { data: deckGroup, isLoading, error } = useDeckGroupSession(groupId, !isChildRoute);

  // If we're on a child route (like /edit), render the child route component
  if (isChildRoute) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className={styles.quizletDetail__loadingContainer}>
        <CircularLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.quizletDetail__errorContainer}>
        <div className={styles.quizletDetail__errorText}>Error: {error.message}</div>
      </div>
    );
  }

  if (!deckGroup || deckGroup.quizlets.length === 0) {
    return (
      <div className={styles.quizletDetail__notFoundContainer}>
        <div className={styles.quizletDetail__notFoundText}>
          Deck group not found or has no quizzes
        </div>
      </div>
    );
  }

  // Combine all questions from all quizlets
  const allQuestions: Question[] = deckGroup.quizlets.flatMap(
    (quizlet) => (quizlet.questions || []) as Question[]
  );

  if (allQuestions.length === 0) {
    return (
      <div className={styles.quizletDetail__notFoundContainer}>
        <div className={styles.quizletDetail__notFoundText}>
          This deck group has no questions
        </div>
      </div>
    );
  }

  const handleExit = () => {
    navigate({
      to: '/quizlets',
      search: { tab: 'grouped' },
    });
  };

  return (
    <div className={styles.quizletDetail}>
      <div className={styles.quizletDetail__header}>
        <div className={styles.quizletDetail__headerContent}>
          <div>
            <h1 className={styles.quizletDetail__title}>{deckGroup.name}</h1>
            <p className={styles.quizletDetail__subtitle}>
              {allQuestions.length} question{allQuestions.length !== 1 ? 's' : ''} from{' '}
              {deckGroup.quizlets.length} deck{deckGroup.quizlets.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Link
            to="/quizlets/deck-groups/$groupId/edit"
            params={{ groupId }}
          >
            <motion.button
              className={styles.quizletDetail__editButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Edit Group
            </motion.button>
          </Link>
        </div>
      </div>

      <QuizSession
        questions={allQuestions}
        title={deckGroup.name}
        isDailyKnowledge={false}
        skipAnalytics={false}
        onExit={handleExit}
      />
    </div>
  );
}
